import { FC, useContext, useRef } from 'react';

import { AddImageIcon, H3, Tooltip } from '@smartshell/ui-kit';

import { useUploadFile } from 'hooks';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';

import s from './AdvertisementImage.module.scss';
import { AdvertisementItem } from './AdvertisementItem';
import { IAdvertisementImageProps } from './interfaces';

export const AdvertisementImage: FC<IAdvertisementImageProps> = ({
  value = [],
  onChange,
  maxCount = 5,
  error,
}) => {
  const { t } = useContext(LocalizationContext);
  const imgInput = useRef<HTMLInputElement>(null);
  const FILE_MAXIMUM_SIZE = 8 * 1024 * 1024;

  const { uploadImages } = useUploadFile(undefined, { multiple: true });

  const remaining = maxCount - value.length;

  return (
    <div className={s.container}>
      <div className={s.header}>
        <div className={s.header_title}>
          <H3>{t('advertisement.manager.form.photo')}</H3>
          <Tooltip
            contentClassName={s.tooltip}
            content={
              <div className={s.tooltip_content}>
                <H3>{t('application.game.image.size.title')}</H3>
                <ul className={s.list}>
                  <li className={s.item}>
                    <span className={s.name}>
                      {t('application.image.minRes')}
                    </span>
                    <span className={s.value}>
                      {t('advertisement.add.photo.tooltip.min')}
                    </span>
                  </li>
                  <li className={s.item}>
                    <span className={s.name}>
                      {t('goods.edit.photo.optimalRes')}
                    </span>
                    <span className={s.value}>5000x5000</span>
                  </li>
                  <li className={s.item}>
                    <span className={s.name}>
                      {t('application.image.sides')}
                    </span>
                    <span className={s.value}>
                      {t('advertisement.add.photo.tooltip.min')}
                    </span>
                  </li>
                  <li className={s.item}>
                    <span className={s.name}>
                      {t('application.image.maxSize')}
                    </span>
                    <span className={s.value}>{t('megaBytes', 8)}</span>
                  </li>
                  <li className={s.item}>
                    <span className={s.name}>
                      {t('application.image.format')}
                    </span>
                    <span className={s.value}>.jpg, .png</span>
                  </li>
                </ul>
              </div>
            }
          />
        </div>
        <span className={s.image_count}>
          {value.length}/{maxCount}
        </span>
      </div>

      <div className={s.image_block}>
        {Boolean(value.length) && (
          <div className={s.preview_block}>
            {value.map((src, index) => (
              <AdvertisementItem
                key={`${index}-${src}`}
                value={src}
                onDelete={() => {
                  const next = value.filter((_, i) => i !== index);
                  onChange?.(next);
                }}
                onReplace={(newUrl) => {
                  const next = [...value];
                  next[index] = newUrl;
                  onChange?.(next);
                }}
              />
            ))}
          </div>
        )}

        {value.length < maxCount && (
          <>
            <div
              className={s.default_image}
              onClick={() => imgInput.current?.click()}
            >
              <AddImageIcon size="l" color="secondary" />
            </div>

            <input
              ref={imgInput}
              type="file"
              multiple
              accept=".png,.jpg,.jpeg"
              hidden
              onChange={async (e) => {
                const files = Array.from(e.target.files ?? []).slice(
                  0,
                  remaining
                );
                e.target.value = '';

                if (!files.length || !uploadImages) return;

                if (files.some((fl) => fl.size > FILE_MAXIMUM_SIZE)) {
                  return notification.info({
                    t,
                    type: 'error',
                    title: t('upload.file.error'),
                    message: t('upload.file.error_2'),
                  });
                }

                try {
                  const urls = await uploadImages(files);
                  onChange?.([...value, ...urls]);
                } catch (err) {
                  notification.info({
                    t,
                    type: 'error',
                    title: t('upload.file.error'),
                    message: String(err),
                  });
                }
              }}
            />
          </>
        )}
      </div>

      {error?.length && <span className={s.error}>{t('errors.required')}</span>}
    </div>
  );
};
